import Cookies from 'js-cookie';
import { PLANS_FEATURES } from 'constants/plans';
import Bugsnag from '@bugsnag/js';
import cache from 'utils/cache';
import { bugsnagNotify } from 'utils/error';

export const SIGNUP_VERSION = 'v6';
export const PRICING_VERSION = '052024';

export interface RequestFormData {
  _id: string;
  plan: string;
  numberAccounts: number;
  currency: string;
  billingPeriod: string;
  coupon_ids?: string[];
}

interface ResponseType {
  url: any;
  status: string;
  message: string;
  data: any;
  csrfReset?: boolean;
  error?: any;
}

// VWO
// @ts-ignore
const VWO = (window.VWO = window.VWO || []);
// @ts-ignore
VWO.event =
  VWO.event ||
  function () {
    VWO.push(['event'].concat([].slice.call(arguments)));
  };
// @ts-ignore
const _hsq = (window._hsq = window._hsq || []);

const runCompletedTags = (adminId: string | undefined, plan: string | undefined) => {
  if (!window.uetq) {
    window.uetq = [];
  }

  window.uetq?.push('event', 'signup-completed', {
    event_category: 'signup',
    event_label: 'signup-completed',
  });
  _hsq?.push([
    'trackEvent',
    {
      id: 'Signup completed (admin)',
    },
  ]);
  _hsq?.push(['identify', { id: adminId }]);

  trackEvent({
    event: 'sign-up-24-step-3-complete',
  });

  window.fbq?.('track', 'StartTrial');

  window.pintrk?.('track', 'checkout', {
    product_id: plan,
  });

  // temporarily run GA4 event tags client-side
  window.gtag?.('event', 'signup-completed', {
    plan,
    adminId,
  });

  window.gtag?.('event', 'conversion', {
    send_to: 'AW-992177465/ZcAhCPGU34gBELnajdkD',
  });
  // linkedin signup conversion ID
  window?.lintrk?.('track', { conversion_id: 613994 });

  trackEvent({
    event: 'sign_up-chargebee_modal-click-next',
    metaData: {
      is_credit_card_provided: true,
    },
  });

  // Replace the property values with your actual values
  window.optibaseSendConversionEvent?.('signupcompleted');
  // conversion in LinkedIn
  window?.lintrk?.('track', { conversion_id: 613994 });
};

export const trackEvent = ({
  event,
  metaData,
  services = ['IC', 'AMP'],
  callbackFn,
}: {
  event: string;
  metaData?: any;
  services?: string[];
  callbackFn?: () => void;
}) => {
  console.log(`[${process.env.NODE_ENV} ${services} track event]`, event, metaData);

  if (process.env.NODE_ENV !== 'production') {
    callbackFn?.();
    return console.info('Not sent because not production env');
  }

  if (services.includes('AMP') && window.amplitude) {
    try {
      window.amplitude?.track?.(event, metaData);
    } catch (error: any) {
      bugsnagNotify(error, 'Error trackEvent Amplitude');
      console.error('Error trackEvent Amplitude', error);
    }
  }

  callbackFn?.();
};

const apiCall = async (method: 'GET' | 'POST', url: string, data: any): Promise<ResponseType> => {
  try {
    const CSRFToken = await getCSRFToken();

    const makeCall = async (token: string): Promise<ResponseType> => {
      try {
        const response = await fetch(url, {
          method,
          headers: {
            ['X-XSRF-TOKEN']: token,
            'Content-Type': 'application/json',
          },
          body: data ? JSON.stringify(data) : undefined,
        });

        const jsonResponse = await response.json();

        if (jsonResponse.status === 'error' && jsonResponse.csrfReset) {
          const freshToken = await getCSRFToken(true);
          return makeCall(freshToken);
        }

        return jsonResponse;
      } catch (error: any) {
        bugsnagNotify(error, `makeCall error: ${url}`);
        throw error;
      }
    };

    return makeCall(CSRFToken);
  } catch (error: any) {
    bugsnagNotify(error, `apiCall call error: ${url}`);
    throw error;
  }
};

export const getPlansFromApi = async ({
  id,
  currency,
  numberAccounts,
  country,
}: {
  id: string;
  currency: string;
  numberAccounts: number;
  country: string;
}) => {
  const cacheKeys = `${SIGNUP_VERSION}-${id}-${currency}-${numberAccounts}-${country}`;
  const cachedPlans = cache.get(cacheKeys);

  if (cachedPlans) {
    return cachedPlans;
  }

  const response = await apiCall('POST', '/signup/v2/getPlans', {
    _id: id,
    currency,
    numberAccounts,
    country,
  });

  let plans = [];
  if (response?.status === 'ok' && response?.data?.plans) {
    plans = response?.data?.plans;
    cache.set(cacheKeys, plans, 3600000); // 1h cashed
  }

  return plans;
};

const formatPrice = (price: number) => {
  return price / 100;
};

export interface PlanType {
  id: string;
  title?: string;
  features?: {
    title: string;
    value: string[];
  };
  price: number;
  currency: string;
  currency_code?: string;
  period: string;
  period_unit?: string;
  shortName: string;
  tooltip: string;
  includedAccounts?: string[];
  sort: number;
  industry?: string;
  additionalAccountsPrice?: number;
  includesGst?: boolean;
  disabled?: boolean;
}

export interface PlansType {
  month: PlanType[];
  year: PlanType[];
}

export const mergeSortPlans = (plans: PlanType[], numberAccounts: number) => {
  const plansByPeriod = plans.reduce(
    (acum: PlansType, plan: any) => {
      const idForFeature: 'sked-enterprise' | 'sked-essentials' | 'sked-professional' =
        plan.id.replace('-annual', '');
      const { features, shortName, sort, tooltip, industry } = PLANS_FEATURES[idForFeature] || {};

      if (!features) {
        return acum;
      }

      const planObj: PlanType = {
        id: plan.id,
        title: plan.name,
        price: formatPrice(plan.price),
        currency: plan.currency_code,
        period: plan.period_unit,
        features,
        shortName,
        includedAccounts: plan.includedAccounts,
        tooltip,
        sort,
        industry,
        additionalAccountsPrice: formatPrice(plan.additionalAccountsPrice),
        includesGst: plan.includesGst,
        disabled: numberAccounts > 20,
      };

      if (planObj.period === 'month') {
        acum.month.push(planObj);
      } else {
        acum.year.push(planObj);
      }

      return acum;
    },
    { month: [], year: [] },
  );

  plansByPeriod.month.sort((a: PlanType, b: PlanType) => a.sort - b.sort);
  plansByPeriod.year.sort((a: PlanType, b: PlanType) => a.sort - b.sort);

  return plansByPeriod;
};

export const getPlans = async ({
  currency,
  id,
  numberAccounts,
  country,
}: {
  currency: string;
  id: string;
  numberAccounts: number;
  country: string;
}) => {
  const plans = await getPlansFromApi({ currency, id, numberAccounts, country });

  return mergeSortPlans(plans, numberAccounts);
};

export const getCSRFToken = async (reset = false) => {
  const cacheKeys = `CSRFToken`;
  const cachedCSRFToken = cache.get(cacheKeys);

  if (!reset && cachedCSRFToken) {
    return cachedCSRFToken;
  }

  const { token, session } = await fetch('/csrf-token', {
    method: 'GET',
  }).then(response => response.json() as Promise<{ token: string; session: boolean }>);

  if (session) {
    window.location.href = '/'; // if has session just global redirect to home page
  }

  cache.set(cacheKeys, token, 30000); // 30s cashed

  return token;
};

interface ErrorWithStatus extends Error {
  status?: number;
}

export const subscribeCard = async (
  _id: string,
  token: string,
  plan: string,
  coupon_ids: string[] | null,
  currency: string,
  billingPeriod: string,
  numberAccounts: number,
) => {
  try {
    const CSRFToken = await getCSRFToken();

    const data = {
      _id,
      token,
      plan,
      coupon_ids,
      currency,
      billingPeriod,
      numberAccounts,
    };

    const response = await fetch('/signup/v2/createSubscriptionWithToken', {
      method: 'POST',
      headers: {
        ['X-XSRF-TOKEN']: CSRFToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (response.status !== 200) {
      /** try to capture error details from server */
      const responseText = await response.text();
      bugsnagNotify(new Error(responseText), 'subscribeCard response error');

      const error = new Error(
        `${extractErrorJson(responseText) || response.statusText}`,
      ) as ErrorWithStatus;
      error.status = response.status;
      throw error;
    }

    return await response.json();
  } catch (error: any) {
    bugsnagNotify(error, 'subscribeCard error');
    throw error;
  }
};

function extractErrorJson(text: string) {
  try {
    const json = JSON.parse(text);
    return json.error || json.message || text;
  } catch (e) {
    return text;
  }
}

export const createPaymentIntent = async ({
  amount,
  currency_code,
}: {
  amount: number;
  currency_code: 'USD';
}) => {
  try {
    const CSRFToken = await getCSRFToken();

    const response = await fetch('/payments/payment-intents', {
      method: 'POST',
      headers: {
        ['X-XSRF-TOKEN']: CSRFToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        currency_code,
        payment_method_type: 'paypal_express_checkout',
      }),
    });

    const responseJson = await response.json();
    return responseJson.payment_intent;
  } catch (error: any) {
    bugsnagNotify(error, 'createPaymentIntent error');
    throw error;
  }
};

export const validateCoupon = async (coupon: string, plan: string, currency: string) => {
  try {
    const CSRFToken = await getCSRFToken();

    const response = await fetch('/payments/coupons/validate', {
      method: 'PUT',
      headers: {
        'X-XSRF-TOKEN': CSRFToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        coupon,
        plan,
        currency,
      }),
    });

    return await response.json();
  } catch (error: any) {
    bugsnagNotify(error, 'validateCoupon error');
    throw error;
  }
};

declare global {
  interface Window {
    willRedirectionOccurByVWO: any;
  }
}

export interface SignupUserProps {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  numberAccounts: number;
  country: string;
  timezone: string;
  _id?: string;
  cbSubId?: string;
}

export const signupUser = async (userInfo: SignupUserProps) => {
  const tracker = {
    tid: Cookies.get('_fprom_track'),
    signupCid: window.ga?.getAll()[0].get('clientId'),
    fbData: {
      fbc: Cookies.get('_fbc'),
      fbp: Cookies.get('_fbp'),
    },
  };
  const pricingVersion = PRICING_VERSION;
  const signupVersion = SIGNUP_VERSION;

  try {
    const result = await apiCall('POST', '/signup/v2/createUser', {
      agreedTos: true,
      pricingVersion,
      signupVersion,
      ...userInfo,
      ...tracker,
    });

    if (result?.status === 'redirect') {
      runCompletedTags(undefined, undefined);
      return (window.location.href = result.url);
    } else if (result?.status !== 'ok') {
      const error = new Error(result?.error || 'An error occurred');
      bugsnagNotify(error, `signupUser result error`);
      throw error;
    } else {
      window.amplitude?.track?.('sign-up-23-complete-step-1');
      window.optibaseSendConversionEvent?.('signupstep1');
      _hsq?.push([
        'trackEvent',
        {
          id: 'Signup step 1',
        },
      ]);
      _hsq?.push(['identify', { email: userInfo.email }]);
      window.gtag?.('set', 'user_data', {
        email: userInfo.email,
        address: {
          country: userInfo.country,
        },
      });
      window.ga?.create('&uid', result.data?.user._id);
      window.ga?.create('userId', result.data?.user._id);

      window.fbq?.('track', 'AddToCart');
      window.gtag?.('event', 'signup-step-1');

      trackEvent({
        event: 'sign-up-24-step-1-complete',
        metaData: {
          numberAccounts: userInfo.numberAccounts,
        },
      });
    }

    return result?.data;
  } catch (error: any) {
    bugsnagNotify(error, `signupUser error`);
    throw error;
  }
};

export const savePlanDetails = async (plan: string, currency: string) => {
  window.optibaseSendConversionEvent?.('signupstep2');
  _hsq?.push([
    'trackEvent',
    {
      id: 'Signup step 2',
    },
  ]);
  window.gtag?.('event', 'signup-step-2', {
    plan,
    currency,
  });

  trackEvent({
    event: 'sign-up-24-step-2-complete',
    metaData: {
      currency,
      plan,
    },
  });
  window.fbq?.('track', 'Lead');
};

export const getPlanEstimate = async (data: RequestFormData) => {
  const { _id, currency, plan, billingPeriod, numberAccounts, coupon_ids } = data;

  try {
    const CSRFToken = await getCSRFToken();
    const response = await fetch('/signup/v2/getPlanEstimate', {
      method: 'POST',
      headers: {
        ['X-XSRF-TOKEN']: CSRFToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        _id,
        plan,
        numberAccounts,
        currency,
        billingPeriod,
        coupon_ids,
      }),
    });

    const jsonResponse = await response.json();

    if (jsonResponse.status === 'ok') {
      return jsonResponse?.data?.estimate?.estimate;
    } else {
      const error = new Error(jsonResponse.error || 'Plan estimate failed');
      bugsnagNotify(error, 'getPlanEstimate Result error');
      throw error;
    }
  } catch (error: any) {
    bugsnagNotify(error, 'getPlanEstimate error');
    throw error;
  }
};

export const signupCompletedAndRedirectToDashboard = (plan: string, adminId?: string) => {
  runCompletedTags(plan, adminId);

  sessionStorage.removeItem('__LSM__');
  sessionStorage.removeItem('skipCreditCardDetail');

  window.location.href = '/dashboard';
};
